import { useAuthStore } from '@/stores/authStore'

/**
 * Client-side mirror of the backend's RBAC permission engine
 * (`backend/app/core/permissions.py`), fed by the map `authStore` fetches
 * once at login/boot from `GET /auth/permissions` (design doc section 6).
 *
 * **UX-only.** This hook exists purely to hide/disable controls a role
 * can't use, so the user doesn't click something that just 403s. It is
 * never the real security boundary -- `require_permission` on every
 * backend route is, and stays the only thing actually enforcing anything.
 * A stale or missing permission map degrades to "not allowed" (hides
 * controls), never to "allowed" -- see `allowed`'s default below.
 */

// Levels that only grant read access -- everything else (manage/all/own/
// edit/draft/gated) is write-capable. Mirrors the "view is read-only, not
// write-capable" convention already established backend-side.
const READ_ONLY_LEVELS = new Set(['view', 'export'])

export interface PermissionCheck {
  /** Whether the current role has any access at all to this (resource, action). */
  allowed: boolean
  /** The raw level string (e.g. "own", "gated", "view"), or null if not allowed. */
  scope: string | null
  /** Whether that access is write-capable, as opposed to read-only ("view"/"export"). */
  canWrite: boolean
}

/**
 * @example
 *   const { allowed } = usePermission('workflows', 'activate')
 *   return allowed ? <ActivateButton /> : null
 *
 * @example
 *   const { canWrite } = usePermission('integrations', 'manage')
 *   return <SaveButton disabled={!canWrite} />
 */
export function usePermission(resource: string, action: string): PermissionCheck {
  const level = useAuthStore((s) => s.permissions[`${resource}.${action}`]) ?? null
  const allowed = level !== null
  return {
    allowed,
    scope: level,
    canWrite: allowed && !READ_ONLY_LEVELS.has(level as string),
  }
}
